/**
 * Indicator settings panel — floating, draggable, non-modal.
 *
 * Scope model (workspace / user) is managed by useScopedIndicators.
 * Workspace changes apply live to the chart and persist via autosave.
 * User-scope changes persist via PUT /api/indicators on Save.
 *
 * Lives in src/indicators/ parallel to src/tools/, sharing the same
 * SettingsPanel shell from tools/_shared.
 */
import { useEffect, useRef, useState } from "react";
import { useScopedIndicators } from "./_shared/useScopedIndicators";
import { SettingsPanel } from "@/tools/_shared/SettingsPanel";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { DateRangePicker } from "@/components/DateTimePicker/DateRangePicker";
import { parseColor, formatColor } from "@/utils/colorWithAlpha";
import {
  DEFAULT_INDICATORS,
  DEFAULT_MA_LINE,
  type IndicatorSettings,
  type MaLine,
} from "@/state/slices/uiSlice";
import { defaultAnchorInstant } from "@/utils/indicators";
import { useAppSelector, useActiveTabId } from "@/state/hooks";
import styles from "@/tools/_shared/panel.module.css";
import local from "./IndicatorsPanel.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

function clamp(raw: string, min: number, max: number, fallback: number): number {
  const v = parseInt(raw, 10);
  return isNaN(v) ? fallback : Math.min(max, Math.max(min, v));
}

function initRawPeriods(lines: MaLine[]): Record<string, string> {
  return Object.fromEntries(lines.map((l) => [l.id, String(l.period)]));
}

// Anchor From/To window — the SAME range control as the analysis range (our
// time-selection standard: `DateRangePicker`, `zone="utc"`, so the picked wall
// clock IS the UTC anchor). One calendar: first click sets the start, the next
// sets the end, times are filled after. Laid out on its own full-width row so
// it never crowds the MA/RSI controls. The end is optional — `Present` in the
// popover leaves it open so the series runs to the live edge.
function AnchorWindow({
  from,
  to,
  onChange,
}: {
  from: string;
  to?: string;
  onChange: (from: string, to: string | undefined) => void;
}) {
  return (
    <div className={local.anchorGroup}>
      <DateRangePicker
        zone="utc"
        allowPresent
        from={from}
        to={to}
        onChange={onChange}
        className={local.dtInput}
        ariaLabel="Anchor window (UTC)"
      />
      <span className={local.utcTag}>UTC</span>
    </div>
  );
}

// MA line type → segmented-button label (AnchoredEMA shortened to fit).
const MA_TYPES: readonly (readonly [MaLine["type"], string])[] = [
  ["SMA", "SMA"],
  ["EMA", "EMA"],
  ["Anchored", "Anch"],
] as const;

export function IndicatorsPanel({ open, onClose }: Props) {
  const hook = useScopedIndicators(open);

  // Default anchor seed = the previous week's session OPEN (the daily-highlight
  // swap time, ~22:00 UTC), derived from THIS symbol's tick boundaries so it
  // stays DST-correct. Falls back to a date-only-ish seed until boundaries load.
  const tabId = useActiveTabId();
  const symbol = useAppSelector((s) => s.ui.byTab[tabId]?.symbol);
  const boundaries = useAppSelector((s) =>
    symbol ? s.sessionBoundaries.bySymbol[symbol] ?? [] : [],
  );
  const defaultAnchorDate = () =>
    defaultAnchorInstant(Math.floor(Date.now() / 1000), boundaries);

  // Raw string states for number inputs — flush to Redux on blur so the
  // user can type freely without triggering a dispatch on every keystroke.
  const [rawPeriods, setRawPeriods] = useState<Record<string, string>>({});
  const [rawRsi, setRawRsi] = useState({
    period: String(DEFAULT_INDICATORS.rsi.period),
    overbought: String(DEFAULT_INDICATORS.rsi.overbought),
    oversold: String(DEFAULT_INDICATORS.rsi.oversold),
  });

  // Whether a number input is being typed in right now. While focused we must
  // NOT clobber the raw draft from the slice (the user is mid-edit); when not
  // focused the inputs should always reflect the current scope's slice values.
  const inputFocusedRef = useRef(false);

  // Keep the raw number-input drafts in sync with the active scope's values.
  // Re-runs whenever those values change from ANY external action — scope
  // switch, Customize, Copy from User, Reset to defaults — guarded so it never
  // overwrites a field the user is actively typing in. A signature of the
  // MA-line + RSI values is the dependency so a reference change alone (with
  // identical values) doesn't churn. This is what stops a stale draft keyed by
  // a reused line id (e.g. "ma-1") from rendering + committing over a freshly
  // seeded value (the Copy-from-User 200→89 bug).
  const maSig = hook.current.maLines
    .map((l) => `${l.id}:${l.period}`)
    .join("|");
  const rsiSig = `${hook.current.rsi.period}|${hook.current.rsi.overbought}|${hook.current.rsi.oversold}`;
  useEffect(() => {
    if (open && !inputFocusedRef.current) syncRaw(hook.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maSig, rsiSig]);

  const syncRaw = (ind: IndicatorSettings) => {
    setRawPeriods(initRawPeriods(ind.maLines));
    setRawRsi({
      period: String(ind.rsi.period),
      overbought: String(ind.rsi.overbought),
      oversold: String(ind.rsi.oversold),
    });
  };

  // ── MA mutations ──

  const patchLine = (id: string, patch: Partial<MaLine>) => {
    hook.patch({
      ...hook.current,
      maLines: hook.current.maLines.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    });
  };

  const commitMaPeriod = (id: string, raw: string) => {
    const v = clamp(raw, 1, 500, DEFAULT_MA_LINE.period);
    setRawPeriods((r) => ({ ...r, [id]: String(v) }));
    patchLine(id, { period: v });
  };

  const addLine = () => {
    const id = `ma-${Date.now()}`;
    setRawPeriods((r) => ({ ...r, [id]: String(DEFAULT_MA_LINE.period) }));
    hook.patch({
      ...hook.current,
      maLines: [...hook.current.maLines, { id, ...DEFAULT_MA_LINE }],
    });
  };

  const removeLine = (id: string) => {
    setRawPeriods((r) => {
      const n = { ...r };
      delete n[id];
      return n;
    });
    hook.patch({
      ...hook.current,
      maLines: hook.current.maLines.filter((l) => l.id !== id),
    });
  };

  // ── RSI mutations ──

  const patchRsi = (patch: Partial<IndicatorSettings["rsi"]>) => {
    hook.patch({ ...hook.current, rsi: { ...hook.current.rsi, ...patch } });
  };

  // ── Anchor-Time RSI mutations (a separate indicator from the period RSI) ──

  const anchorRsi = hook.current.anchorRsi ?? DEFAULT_INDICATORS.anchorRsi;
  const patchAnchorRsi = (patch: Partial<IndicatorSettings["anchorRsi"]>) => {
    hook.patch({ ...hook.current, anchorRsi: { ...anchorRsi, ...patch } });
  };

  // ── Save / Cancel ──

  const handleSave = async () => {
    // Flush pending unblurred number inputs before persisting.
    const finalLines = hook.current.maLines.map((line) => ({
      ...line,
      period: clamp(
        rawPeriods[line.id] ?? String(line.period),
        1,
        500,
        DEFAULT_MA_LINE.period,
      ),
    }));
    const finalRsi = {
      ...hook.current.rsi,
      period: clamp(rawRsi.period, 1, 200, DEFAULT_INDICATORS.rsi.period),
      overbought: clamp(rawRsi.overbought, 50, 99, DEFAULT_INDICATORS.rsi.overbought),
      oversold: clamp(rawRsi.oversold, 1, 49, DEFAULT_INDICATORS.rsi.oversold),
    };
    const final: IndicatorSettings = {
      maLines: finalLines,
      rsi: finalRsi,
      anchorRsi,
    };
    hook.patch(final);
    await hook.save(final);
    onClose();
  };

  const handleCancel = () => {
    hook.cancel();
    onClose();
  };

  return (
    <SettingsPanel
      open={open}
      title="Indicators"
      scope={hook.scopeUi}
      actions={hook.actions}
      saving={hook.saving}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {hook.isInheriting ? (
        /* Workspace scope, no tab override → this chart has NO indicators
           (user scope is a saved template, it does not apply live). The CTA
           creates a per-chart override seeded from the User-defaults template. */
        <div className={local.inheritingBanner}>
          <span className={local.inheritingLabel}>
            No indicators on this chart
          </span>
          <button
            type="button"
            className={local.customizeBtn}
            onClick={hook.customize}
          >
            Add indicators (copy from User defaults)
          </button>
        </div>
      ) : (
        <>
          {/* ── Moving Averages ── */}
          <p className={styles.globalLabel}>Moving Averages</p>

          {hook.current.maLines.length > 0 && (
            <div className={styles.grid}>
              {hook.current.maLines.map((line) => {
                const { color: maHex, opacity: maOpacity } = parseColor(line.color);
                return (
                  <div key={line.id} className={local.maRow}>
                    <input
                      type="checkbox"
                      className={styles.check}
                      checked={line.enabled}
                      onChange={(e) => patchLine(line.id, { enabled: e.target.checked })}
                      aria-label="Enable MA line"
                    />
                    <div className={`${styles.segmented} ${local.maSegmented}`}>
                      {MA_TYPES.map(([t, label]) => (
                        <button
                          key={t}
                          type="button"
                          className={`${styles.segment} ${line.type === t ? styles.segmentActive : ""}`}
                          onClick={() =>
                            patchLine(
                              line.id,
                              t === "Anchored" && !line.anchorDate
                                ? { type: t, anchorDate: defaultAnchorDate() }
                                : { type: t },
                            )
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className={local.maPeriodCell}>
                      {line.type === "Anchored" ? (
                        /* Anchored MA has no period — it averages every bar in
                           the window; the From/To range renders on its own row
                           below (see anchorRangeRow). */
                        <span className={local.anchorNoPeriod}>—</span>
                      ) : (
                        <input
                          type="number"
                          className={`${styles.numInput} ${local.period}`}
                          min={1}
                          max={500}
                          value={rawPeriods[line.id] ?? String(line.period)}
                          onFocus={() => {
                            inputFocusedRef.current = true;
                          }}
                          onChange={(e) =>
                            setRawPeriods((r) => ({ ...r, [line.id]: e.target.value }))
                          }
                          onBlur={(e) => {
                            inputFocusedRef.current = false;
                            commitMaPeriod(line.id, e.target.value);
                          }}
                        />
                      )}
                    </div>
                    <ColorPicker
                      value={maHex}
                      opacity={maOpacity}
                      onChange={(c, o) => patchLine(line.id, { color: formatColor(c, o) })}
                      ariaLabel={`Color for MA ${line.period}`}
                    />
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => removeLine(line.id)}
                      title="Remove line"
                      aria-label="Remove MA line"
                    >
                      ×
                    </button>
                    {line.type === "Anchored" ? (
                      <div className={local.anchorRangeRow}>
                        <AnchorWindow
                          from={line.anchorDate ?? defaultAnchorDate()}
                          to={line.endDate}
                          onChange={(f, t) =>
                            patchLine(line.id, { anchorDate: f, endDate: t })
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          <button type="button" className={local.addLineBtn} onClick={addLine}>
            + Add line
          </button>

          {/* ── RSI ── */}
          <div className={local.divider} />

          <div className={local.rsiHead}>
            <div className={local.rsiLabelGroup}>
              <span className={local.rsiName}>RSI</span>
              <span className={local.rsiDesc}>Relative Strength Index</span>
            </div>
            <input
              type="checkbox"
              className={local.switch}
              checked={hook.current.rsi.enabled}
              onChange={(e) => patchRsi({ enabled: e.target.checked })}
              aria-label="Enable RSI"
            />
          </div>

          <div className={hook.current.rsi.enabled ? local.rsiFieldsEnabled : local.rsiFields}>
            <div className={styles.lineRow}>
              <span className={styles.lineRowLabel}>Period</span>
              <input
                type="number"
                className={`${styles.numInput} ${local.rsiInput}`}
                min={1}
                max={200}
                value={rawRsi.period}
                onFocus={() => {
                  inputFocusedRef.current = true;
                }}
                onChange={(e) => setRawRsi((r) => ({ ...r, period: e.target.value }))}
                onBlur={(e) => {
                  inputFocusedRef.current = false;
                  const v = clamp(e.target.value, 1, 200, DEFAULT_INDICATORS.rsi.period);
                  setRawRsi((r) => ({ ...r, period: String(v) }));
                  patchRsi({ period: v });
                }}
              />
            </div>
            <div className={styles.lineRow}>
              <span className={styles.lineRowLabel}>Overbought</span>
              <input
                type="number"
                className={`${styles.numInput} ${local.rsiInput}`}
                min={50}
                max={99}
                value={rawRsi.overbought}
                onFocus={() => {
                  inputFocusedRef.current = true;
                }}
                onChange={(e) => setRawRsi((r) => ({ ...r, overbought: e.target.value }))}
                onBlur={(e) => {
                  inputFocusedRef.current = false;
                  const v = clamp(e.target.value, 50, 99, DEFAULT_INDICATORS.rsi.overbought);
                  setRawRsi((r) => ({ ...r, overbought: String(v) }));
                  patchRsi({ overbought: v });
                }}
              />
            </div>
            <div className={styles.lineRow}>
              <span className={styles.lineRowLabel}>Oversold</span>
              <input
                type="number"
                className={`${styles.numInput} ${local.rsiInput}`}
                min={1}
                max={49}
                value={rawRsi.oversold}
                onFocus={() => {
                  inputFocusedRef.current = true;
                }}
                onChange={(e) => setRawRsi((r) => ({ ...r, oversold: e.target.value }))}
                onBlur={(e) => {
                  inputFocusedRef.current = false;
                  const v = clamp(e.target.value, 1, 49, DEFAULT_INDICATORS.rsi.oversold);
                  setRawRsi((r) => ({ ...r, oversold: String(v) }));
                  patchRsi({ oversold: v });
                }}
              />
            </div>
            <div className={styles.lineRow}>
              <span className={styles.lineRowLabel}>Color</span>
              {(() => {
                const { color: rsiHex, opacity: rsiOpacity } = parseColor(
                  hook.current.rsi.color,
                );
                return (
                  <ColorPicker
                    value={rsiHex}
                    opacity={rsiOpacity}
                    onChange={(c, o) => patchRsi({ color: formatColor(c, o) })}
                    ariaLabel="RSI color"
                  />
                );
              })()}
            </div>
          </div>

          {/* ── Anchor-Time RSI (separate indicator) ── */}
          <div className={local.divider} />

          <div className={local.rsiHead}>
            <div className={local.rsiLabelGroup}>
              <span className={local.rsiName}>Anchor-Time RSI</span>
              <span className={local.rsiDesc}>Cumulative RSI from a date</span>
            </div>
            <input
              type="checkbox"
              className={local.switch}
              checked={anchorRsi.enabled}
              onChange={(e) =>
                patchAnchorRsi(
                  e.target.checked && !anchorRsi.anchorDate
                    ? { enabled: true, anchorDate: defaultAnchorDate() }
                    : { enabled: e.target.checked },
                )
              }
              aria-label="Enable Anchor-Time RSI"
            />
          </div>

          <div className={anchorRsi.enabled ? local.rsiFieldsEnabled : local.rsiFields}>
            <div className={styles.lineRow}>
              <span className={styles.lineRowLabel}>Window</span>
              <AnchorWindow
                from={anchorRsi.anchorDate ?? defaultAnchorDate()}
                to={anchorRsi.endDate}
                onChange={(f, t) => patchAnchorRsi({ anchorDate: f, endDate: t })}
              />
            </div>
            <div className={styles.lineRow}>
              <span className={styles.lineRowLabel}>Color</span>
              {(() => {
                const { color: aHex, opacity: aOpacity } = parseColor(anchorRsi.color);
                return (
                  <ColorPicker
                    value={aHex}
                    opacity={aOpacity}
                    onChange={(c, o) => patchAnchorRsi({ color: formatColor(c, o) })}
                    ariaLabel="Anchor RSI color"
                  />
                );
              })()}
            </div>
          </div>
        </>
      )}
    </SettingsPanel>
  );
}
